import React, { useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Form,
  Button,
  Card,
  Spinner,
  InputGroup,
} from "react-bootstrap";
import {
  Key,
  CheckCircle,
  Calendar,
  EnvelopeFill,
  Eye,
  EyeSlash,
} from "react-bootstrap-icons";
import FromPageIndicator from "../Common/FromPageIndicator";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import CustomAlert from "../CustomAlert/CustomAlert";

const LoginPage = () => {
  const navigate = useNavigate();
  const [loginData, setLoginData] = useState({
    email: "",
    password: "",
    rememberMe: false,
  });

  // Alert states
  const [alertConfig, setAlertConfig] = useState({
    show: false,
    variant: "info",
    title: "",
    message: "",
    icon: null,
    autoClose: false,
    autoCloseDelay: 3000,
    position: "center",
  });

  const [isLoading, setIsLoading] = useState(false);
  const [showLoginPassword, setShowLoginPassword] = useState(false);

  const handleChange = (e) => {
    const value =
      e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setLoginData({
      ...loginData,
      [e.target.name]: value,
    });
  };

  const togglePasswordVisibility = () => {
    setShowLoginPassword(!showLoginPassword);
  };

  // Function to show alert
  const showAlert = (config) => {
    setAlertConfig({
      show: true,
      ...config,
    });
  };

  // Function to hide alert
  const hideAlert = () => {
    setAlertConfig((prev) => ({ ...prev, show: false }));
  };

  // Function to authenticate user
  const authenticateUser = async (credentials) => {
    setIsLoading(true);

    try {
      const response = await axios.get(
        "https://api.dremerz.net/api/DZ_8921_Ampicon_Create_Users_UserList"
      );

      const data = response.data;
      const user =
        data &&
        data.find(
          (u) =>
            u.emailId === credentials.email &&
            u.password === credentials.password
        );

      if (user) {
        console.log(user);

        // Store user information in sessionStorage and localStorage
        sessionStorage.setItem("userId", user.id);
        localStorage.setItem("userId", user.id);
        sessionStorage.setItem("userName", user.userName);
        localStorage.setItem("userName", user.userName);
        sessionStorage.setItem("emailId", user.emailId);
        localStorage.setItem("emailId", user.emailId);
        sessionStorage.setItem(
          "registrationCategory",
          user.registrationCategory
        );
        localStorage.setItem("registrationCategory", user.registrationCategory);

        // If remember me is checked, store in localStorage
        if (credentials.rememberMe) {
          localStorage.setItem("rememberedEmail", user.emailId);
        } else {
          localStorage.removeItem("rememberedEmail");
        }

        return { success: true, data };
      } else {
        return { success: false, error: "Invalid email or password" };
      }
    } catch (error) {
      console.error("Login error:", error);
      return { success: false, error: "Please register to login" };
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!loginData.email || !loginData.password) {
      showAlert({
        variant: "warning",
        title: "Missing Information",
        message: "Please enter both email and password",
        icon: "⚠️",
        autoClose: true,
      });
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(loginData.email)) {
      showAlert({
        variant: "danger",
        title: "Invalid Email",
        message: "Please enter a valid email address",
        icon: "❌",
        autoClose: true,
      });
      return;
    }

    const result = await authenticateUser(loginData);

    if (result.success) {
      showAlert({
        variant: "success",
        title: "Login Successful!",
        message: "Welcome back! Redirecting to your profile...",
        icon: "🎉",
        autoClose: true,
        autoCloseDelay: 2000,
      });

      // Redirect to dashboard or user profile
      setTimeout(() => {
        navigate("/userProfile");
        window.location.reload();
      }, 2500);
    } else {
      showAlert({
        variant: "danger",
        title: "Login Failed",
        message: result.error || "Login failed. Please check your credentials.",
        icon: "🚫",
        autoClose: true,
        autoCloseDelay: 4000,
      });
    }
  };

  React.useEffect(() => {
    const rememberedEmail = localStorage.getItem("rememberedEmail");
    if (rememberedEmail) {
      setLoginData((prev) => ({
        ...prev,
        email: rememberedEmail,
        rememberMe: true,
      }));
    }
  }, []);

  return (
    <div style={{ backgroundColor: "#FFF4FA" }}>
      <style>
        {`
          .custom-nav-link.active {
            background-color: #2582A1 !important;
            border-color: #2582A1 !important;
          }
          .custom-nav-link:not(.active) {
            color: #2582A1 !important;
          }
          .form-control:focus {
            border-color: rgba(37, 130, 161, 0.4);
            box-shadow: 0 0 0 0.25rem rgba(37, 130, 161, 0.25);
          }
          .btn-primary {
            background-color: #2582A1;
            border-color: #2582A1;
          }
          .btn-primary:hover,
          .btn-primary:focus {
            background-color: #1f6b86;
            border-color: #1f6b86;
          }
          .btn-outline-primary {
            color: #2582A1;
            border-color: #2582A1;
          }
          .btn-outline-primary:hover {
            background-color: #2582A1;
            border-color: #2582A1;
          }
          .link-primary {
            color: #2582A1 !important;
            cursor: pointer;
          }
          .link-primary:hover {
            text-decoration: underline !important;
          }
          .password-toggle-btn {
            background: none;
            border: none;
            color: #6c757d;
            cursor: pointer;
            position: absolute;
            right: 10px;
            top: 50%;
            transform: translateY(-50%);
            z-index: 10;
            padding: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            width: 30px;
            height: 30px;
          }
          .password-toggle-btn:hover {
            color: #2582A1;
          }
          .password-input-container {
            position: relative;
          }
        `}
      </style>

      {/* Custom Alert Component */}
      <CustomAlert
        show={alertConfig.show}
        onClose={hideAlert}
        variant={alertConfig.variant}
        title={alertConfig.title}
        message={alertConfig.message}
        icon={alertConfig.icon}
        autoClose={alertConfig.autoClose}
        autoCloseDelay={alertConfig.autoCloseDelay}
        position={alertConfig.position}
        animation="bounce"
        overlay={true}
      />

      <FromPageIndicator />

      <div className="text-dark py-4 mb-4">
        <Container>
          <h1 className="text-center mb-2" style={{ color: "#2582A1" }}>
            TN PY AMPICON 2025 Login
          </h1>
          <p className="text-center mb-0">
            28th Annual Conference of Association of Medical Physicists of India
            (Tamil Nadu & Puducherry Chapter)
          </p>
        </Container>
      </div>

      <Container className="pb-5">
        <Row className="justify-content-center">
          <Col lg={5} md={8} className="mb-4">
            <Card className="border-0 shadow-sm">
              <Card.Header className="bg-white border-bottom-0 pt-4 px-4">
                <h3 className="mb-0 text-center">Login to Your Account</h3>
              </Card.Header>
              <Card.Body className="px-4 pb-4">
                <Form onSubmit={handleSubmit} className="px-lg-4">
                  <Form.Group controlId="loginEmail" className="mb-4">
                    <Form.Label className="fw-semibold">
                      Email Address <span className="text-danger">*</span>
                    </Form.Label>
                    <div className="input-group">
                      <span className="input-group-text bg-light">
                        <EnvelopeFill />
                      </span>
                      <Form.Control
                        type="email"
                        placeholder="Enter your email address"
                        name="email"
                        value={loginData.email}
                        onChange={handleChange}
                        required
                        className="py-2"
                      />
                    </div>
                  </Form.Group>

                  <Form.Group controlId="loginPassword" className="mb-4">
                    <Form.Label className="fw-semibold">
                      Password <span className="text-danger">*</span>
                    </Form.Label>
                    <InputGroup>
                      <InputGroup.Text>
                        <Key />
                      </InputGroup.Text>
                      <Form.Control
                        type={showLoginPassword ? "text" : "password"}
                        placeholder="Enter your password"
                        name="password"
                        value={loginData.password}
                        onChange={handleChange}
                        required
                      />
                      <Button
                        variant="outline-secondary"
                        onClick={togglePasswordVisibility}
                        style={{ cursor: "pointer" }}
                      >
                        {showLoginPassword ? <EyeSlash /> : <Eye />}
                      </Button>
                    </InputGroup>
                  </Form.Group>

                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <Button onClick={() => navigate("/forgetPassword")}>
                      Forgot Password?
                    </Button>
                  </div>

                  <div className="d-grid">
                    <Button
                      type="submit"
                      size="lg"
                      className="py-2 btn-primary"
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <>
                          <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            className="me-2"
                          />
                          Logging in...
                        </>
                      ) : (
                        "Login"
                      )}
                    </Button>
                  </div>
                </Form>

                <div className="text-center mt-4 pt-3 border-top">
                  <p className="mb-0">
                    Don't have an account?{" "}
                    <Link
                      to="/registration"
                      className="link-primary text-decoration-none"
                    >
                      Register Now
                    </Link>
                  </p>
                </div>

                <div className="text-center mt-4">
                  <p className="text-muted">
                    By logging in, you agree to our Terms of Service and Privacy
                    Policy
                  </p>
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col lg={4} md={8}>
            <div className="sticky-top" style={{ top: "2rem" }}>
              <Card className="border-0 shadow-sm mb-4">
                <Card.Header className="bg-white border-bottom-0 pt-4 px-4">
                  <h3 className="mb-0">Conference Highlights</h3>
                </Card.Header>
                <Card.Body className="px-4 pb-4">
                  <div className="alert alert-info d-flex align-items-center mb-4">
                    <Calendar className="me-2" size={20} />
                    <div>
                      <strong>Conference Dates</strong>
                      <p className="mb-0">26th and 27th September 2025</p>
                    </div>
                  </div>

                  <ul className="list-unstyled mb-4">
                    <li className="d-flex align-items-center mb-3">
                      <CheckCircle className="text-success me-2" />
                      <span>
                        <strong>Pre-conference Workshop:</strong> Hands-on
                        experience with advanced medical physics techniques
                      </span>
                    </li>
                    <li className="d-flex align-items-center mb-3">
                      <CheckCircle className="text-success me-2" />
                      <span>
                        <strong>Scientific Sessions:</strong> Latest advances in
                        medical physics research and clinical applications
                      </span>
                    </li>
                    <li className="d-flex align-items-center mb-3">
                      <CheckCircle className="text-success me-2" />
                      <span>
                        <strong>Venue:</strong> Auditorium, Apollo Cancer Centre
                        - Vanagaram, Chennai
                      </span>
                    </li>
                    <li className="d-flex align-items-center mb-3">
                      <CheckCircle className="text-success me-2" />
                      <span>
                        <strong>Theme:</strong> "Solving New Challenges in
                        Personalised Oncology"
                      </span>
                    </li>
                  </ul>

                  <div className="d-grid">
                    <Button
                      variant="outline-primary"
                      as={Link}
                      to="/registration"
                      className="d-flex align-items-center justify-content-center"
                    >
                      Register for the Conference
                    </Button>
                  </div>
                </Card.Body>
              </Card>

              <Card className="border-0 shadow-sm bg-light">
                <Card.Body className="p-4">
                  <h5>Need Help?</h5>
                  <p className="mb-0">For login assistance, contact us at:</p>
                  <p className="mb-0">
                    <strong>Email:</strong>{" "}
                    <a href="mailto:tnpyampicon2025@gmail.com">
                      tnpyampicon2025@gmail.com
                    </a>
                  </p>

                  <p className="mb-0">
                    <strong>Phone:</strong> +91 9994432135
                  </p>
                </Card.Body>
              </Card>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default LoginPage;
